import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "../../BottomSheet";
import { useScrollOnExpand } from "../../hooks/useScrollOnExpand";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BadgeIcon,
  CheckIcon,
  ChevronRightIcon,
  FilterIcon,
} from "../../Icons";
import { MobileDateRangeBottomSheet } from "../../MobileDateRange/MobileDateRangeBottomSheet";
import { SearchInput } from "../../SearchInput";
import { Typo } from "../../Typo";

type MobileFiltersBottomSheetV2Props = {
  handleSearchInput: (value: string) => void;
  canViewPII: boolean;
  searchQuery: string;
  countryOptions: { label: string; value: string }[];
  selectedCountryCodes: string[];
  selectedDateOption: string;
  onApply: (params: {
    dateOption: string;
    dateRange: { from: Date; to: Date };
    countryCodes: string[];
  }) => void;
  onReset: () => void;
};

type DateOption = {
  id: string;
  label?: string;
  days?: number;
  type?: "today" | "yesterday" | "days" | "month" | "lastMonth" | "custom";
};

const formatDateShort = (date: Date, locale?: string) => {
  const day = date.getDate();
  const month = date.toLocaleString(locale, { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export const MobileFiltersBottomSheetV2 = ({
  handleSearchInput,
  canViewPII,
  searchQuery,
  countryOptions,
  selectedCountryCodes,
  selectedDateOption,
  onApply,
  onReset,
}: MobileFiltersBottomSheetV2Props) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isDateRangeSheetOpen, setIsDateRangeSheetOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isCountryExpanded, setIsCountryExpanded] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date;
    to: Date;
  } | null>(null);

  const { t, i18n } = useTranslation();
  const translatedContent = useMemo(
    () => ({
      today: t("leadsPage_datepicker_shortcuts_today"),
      yesterday: t("leadsPage_datepicker_shortcuts_yesterday"),
      last7days: t("leadsPage_filters_bottomsheet_last_7_days"),
      last30days: t("leadsPage_filters_bottomsheet_last_30_days"),
      thisMonth: t("leadsPage_datepicker_shortcuts_thismonth"),
      lastMonth: t("leadsPage_datepicker_shortcuts_lastmonth"),
      search: t("leadsPage_input_placeholder_v2_search"),
      searchNoPII: t("leadsPage_input_placeholder_v2_search_no_pii"),
      reset: t("leadsPage_filters_bottomsheet_reset"),
      apply: t("leadsPage_filters_bottomsheet_apply"),
      dates: t("leadsPage_filters_bottomsheet_dates"),
      datesDescription: t("leadsPage_filters_bottomsheet_dates_description"),
      customDateRange: t("leadsPage_filters_bottomsheet_customDateRange"),
      country: t("leadsPage_filters_bottomsheet_country"),
      all: t("leadsPage_filters_bottomsheet_all"),
      selected: t("leadsPage_filters_bottomsheet_selected"),
      selectAll: t("leadsPage_filters_bottomsheet_select_all"),
      title: t("leadsPage_filters_bottomsheet_title"),
    }),
    [t],
  );

  const [localSelectedDates, setLocalSelectedDates] =
    useState<string>(selectedDateOption);
  const [localSelectedCountryCodes, setLocalSelectedCountryCodes] =
    useState<string[]>(selectedCountryCodes);

  const countryWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearch(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    setLocalSelectedCountryCodes(selectedCountryCodes);
  }, [selectedCountryCodes]);

  useScrollOnExpand(isCountryExpanded, countryWrapperRef);

  const dateOptions: DateOption[] = useMemo(
    () => [
      { id: "today", label: translatedContent.today, type: "today" },
      {
        id: "yesterday",
        label: translatedContent.yesterday,
        type: "yesterday",
      },
      {
        id: "last7days",
        label: translatedContent.last7days,
        days: 7,
        type: "days",
      },
      {
        id: "last30days",
        label: translatedContent.last30days,
        days: 30,
        type: "days",
      },
      { id: "thisMonth", label: translatedContent.thisMonth, type: "month" },
      {
        id: "lastMonth",
        label: translatedContent.lastMonth,
        type: "lastMonth",
      },
      {
        id: "custom",
        label: translatedContent.customDateRange,
        type: "custom",
      },
    ],
    [
      translatedContent.today,
      translatedContent.yesterday,
      translatedContent.last7days,
      translatedContent.last30days,
      translatedContent.thisMonth,
      translatedContent.lastMonth,
      translatedContent.customDateRange,
    ],
  );

  const searchTitle = canViewPII
    ? translatedContent.search
    : translatedContent.searchNoPII;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput(value);
  };

  const handleOpenBottomSheet = () => {
    setLocalSelectedDates(selectedDateOption);
    setLocalSelectedCountryCodes(selectedCountryCodes);
    setIsBottomSheetOpen(true);
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
  };

  const handleDateSelection = (optionId: string) => {
    if (optionId === "custom") {
      // Replace the Filters sheet with the Date Range sheet
      setIsBottomSheetOpen(false);
      setIsDateRangeSheetOpen(true);
      return;
    }
    setLocalSelectedDates(optionId);
  };

  const handleDateRangeBack = () => {
    setIsDateRangeSheetOpen(false);
    setIsBottomSheetOpen(true);
  };

  const handleDateRangeClose = () => {
    setIsDateRangeSheetOpen(false);
  };

  const handleDateRangeApply = (range: { from: Date; to: Date }) => {
    setCustomDateRange(range);
    setLocalSelectedDates("custom");
    setIsDateRangeSheetOpen(false);
    setIsBottomSheetOpen(true);
  };

  const handleCountryToggle = (countryCode: string) => {
    if (localSelectedCountryCodes.includes(countryCode)) {
      setLocalSelectedCountryCodes(
        localSelectedCountryCodes.filter((c) => c !== countryCode),
      );
    } else {
      setLocalSelectedCountryCodes([...localSelectedCountryCodes, countryCode]);
    }
  };

  const handleSelectAllCountries = () => {
    if (localSelectedCountryCodes.length === countryOptions.length) {
      setLocalSelectedCountryCodes([]);
    } else {
      setLocalSelectedCountryCodes(countryOptions.map((c) => c.value));
    }
  };

  const handleReset = () => {
    setCustomDateRange(null);
    setLocalSelectedDates("last30days");
    onReset();
    handleCloseBottomSheet();
  };

  const handleApply = () => {
    const selectedOption = dateOptions.find(
      (opt) => opt.id === localSelectedDates,
    );
    if (selectedOption) {
      const today = new Date();
      let from: Date;
      let to: Date;

      switch (selectedOption.type) {
        case "custom": {
          if (customDateRange) {
            from = customDateRange.from;
            to = customDateRange.to;
          } else {
            // Fallback — shouldn't normally happen since picking custom
            // always goes through the date range sheet first
            const start = new Date(today);
            start.setDate(start.getDate() - 30);
            from = start;
            to = today;
          }
          break;
        }
        case "today":
          from = today;
          to = today;
          break;
        case "yesterday": {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          from = yesterday;
          to = yesterday;
          break;
        }
        case "days": {
          const start = new Date(today);
          start.setDate(start.getDate() - (selectedOption.days || 30));
          from = start;
          to = today;
          break;
        }
        case "month": {
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          from = firstDay;
          to = today;
          break;
        }
        case "lastMonth": {
          const firstDayOfLastMonth = new Date(
            today.getFullYear(),
            today.getMonth() - 1,
            1,
          );
          const lastDayOfLastMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            0,
          );
          from = firstDayOfLastMonth;
          to = lastDayOfLastMonth;
          break;
        }
        default: {
          const start = new Date(today);
          start.setDate(start.getDate() - 30);
          from = start;
          to = today;
        }
      }

      onApply({
        dateOption: localSelectedDates,
        dateRange: { from, to },
        countryCodes: localSelectedCountryCodes,
      });
    }

    handleCloseBottomSheet();
  };

  const allCountriesSelected = useMemo(
    () => localSelectedCountryCodes.length === countryOptions.length,
    [localSelectedCountryCodes.length, countryOptions.length],
  );

  const selectedCountryLabel = useMemo(() => {
    if (allCountriesSelected) {
      return translatedContent.all;
    }
    return `${localSelectedCountryCodes.length} ${translatedContent.selected}`;
  }, [
    allCountriesSelected,
    localSelectedCountryCodes.length,
    translatedContent.all,
    translatedContent.selected,
  ]);

  return (
    <div
      data-testid="leads-mobile-filters-bottom-sheet"
      className="flex flex-row items-center gap-2"
    >
      <div className="flex-grow [&_input::placeholder]:!text-sm [&_input]:!text-sm">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder={searchTitle}
        />
      </div>

      <div className="relative">
        <button
          onClick={handleOpenBottomSheet}
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 transition-colors hover:bg-blue-700"
          aria-label="Open filters"
        >
          <FilterIcon className="h-6 w-6 text-text-inverse-primary" />
        </button>
        <div className="absolute -right-0.5 -top-0.5">
          <BadgeIcon className="h-2 w-2" />
        </div>
      </div>

      <BottomSheet
        isOpen={isBottomSheetOpen}
        onClose={handleCloseBottomSheet}
        title={translatedContent.title}
        footer={
          <div className="flex gap-3">
            <button
              className="flex h-10 w-full cursor-pointer items-center justify-center rounded-lg border border-stroke-primary bg-transparent lg:min-w-20 lg:px-3 lg:py-2"
              onClick={handleReset}
            >
              <Typo type="labelSM">{translatedContent.reset}</Typo>
            </button>
            <button
              disabled={localSelectedCountryCodes.length === 0}
              className={clsx(
                "flex h-10 w-full items-center justify-center rounded-lg lg:min-w-20 lg:px-3 lg:py-2",
                localSelectedCountryCodes.length === 0
                  ? "cursor-not-allowed bg-surface-brand-default opacity-40"
                  : "cursor-pointer bg-surface-brand-default",
              )}
              onClick={handleApply}
            >
              <Typo type="labelSM" textColor="text-text-inverse-primary">
                {translatedContent.apply}
              </Typo>
            </button>
          </div>
        }
      >
        <div className="mb-4">
          <div className="mb-1">
            <Typo type="labelSM" textColor="text-text-primary">
              {translatedContent.dates}
            </Typo>
          </div>
          <div className="mb-3">
            <Typo type="bodyXS" textColor="text-text-tertiary">
              {translatedContent.datesDescription}
            </Typo>
          </div>

          <div className="space-y-3">
            {dateOptions.map((option) => {
              const isCustom = option.type === "custom";
              const customSubtitle =
                isCustom && customDateRange
                  ? `${formatDateShort(customDateRange.from, i18n.language)} – ${formatDateShort(customDateRange.to, i18n.language)}`
                  : null;

              return (
                <label
                  key={option.id}
                  className="flex cursor-pointer items-center gap-3"
                  onClick={
                    isCustom ? () => handleDateSelection("custom") : undefined
                  }
                >
                  <div className="relative">
                    <input
                      type="radio"
                      name="dateRange"
                      value={option.id}
                      checked={localSelectedDates === option.id}
                      onChange={() => handleDateSelection(option.id)}
                      className="peer sr-only"
                    />
                    <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-stroke-primary bg-surface-primary peer-checked:border-surface-inverse-primary peer-checked:bg-surface-inverse-primary">
                      {localSelectedDates === option.id && (
                        <div className="h-3 w-3 rounded-full bg-surface-primary" />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col gap-0.5">
                    <Typo type="bodySM" textColor="text-text-primary">
                      {option.label}
                    </Typo>
                    {customSubtitle && (
                      <Typo type="bodyXS" textColor="text-text-tertiary">
                        {customSubtitle}
                      </Typo>
                    )}
                  </div>
                  {isCustom && (
                    <ChevronRightIcon
                      className="h-4 w-4 text-icon-primary"
                      aria-hidden="true"
                    />
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <hr className="border-stroke-primary" />

        <div className="pt-4">
          <div
            className="my-2 flex items-center justify-between"
            onClick={() => setIsCountryExpanded(!isCountryExpanded)}
          >
            <Typo type="labelSM" textColor="text-text-primary">
              {translatedContent.country}
            </Typo>
            <button className="flex items-center gap-1">
              <Typo type="bodySM" textColor="text-text-selected">
                {selectedCountryLabel}
              </Typo>
              {isCountryExpanded ? (
                <ArrowUpIcon className="h-2 w-2" />
              ) : (
                <ArrowDownIcon className="h-2 w-2" />
              )}
            </button>
          </div>

          <div
            ref={countryWrapperRef}
            className={clsx([
              "grid transition-[grid-template-rows] duration-300 ease-out",
              isCountryExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            ])}
          >
            <div
              className={clsx([
                "overflow-hidden",
                isCountryExpanded ? "mt-2" : "mt-0",
              ])}
            >
              <div className="space-y-0">
                <label className="block">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={allCountriesSelected}
                        onChange={handleSelectAllCountries}
                        className="peer sr-only"
                      />
                      <div className="flex h-6 w-6 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                        {allCountriesSelected && (
                          <CheckIcon className="text-text-inverse-primary" />
                        )}
                      </div>
                    </div>
                    <Typo type="bodySM">{translatedContent.selectAll}</Typo>
                  </div>
                </label>

                {countryOptions.map((country) => (
                  <label key={country.value} className="block pt-3">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={localSelectedCountryCodes.includes(
                            country.value,
                          )}
                          onChange={() => handleCountryToggle(country.value)}
                          className="peer sr-only"
                        />
                        <div className="flex h-6 w-6 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                          {localSelectedCountryCodes.includes(
                            country.value,
                          ) && (
                            <CheckIcon className="text-text-inverse-primary" />
                          )}
                        </div>
                      </div>
                      <Typo type="bodySM">{country.label}</Typo>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </BottomSheet>

      <MobileDateRangeBottomSheet
        isOpen={isDateRangeSheetOpen}
        onClose={handleDateRangeClose}
        onBack={handleDateRangeBack}
        onApply={handleDateRangeApply}
        initialRange={customDateRange}
      />
    </div>
  );
};
